// Book Health (C8) — the View → Administration surface that makes the Cause-3
// class visible: which Active desk users have an empty book, which grid manager
// strings resolve to no (or two) IMs, and which stored attributions have gone
// stale against what the shared grid-string rule resolves TODAY.
//
// Read-only; no migration, no Object Type. Computed live from the `users`
// directory, `manager_aliases`, `all_clients` roster and `ontology_clients`
// canonical attribution — so it never drifts from what ingest / the backfill
// would stamp.

import { z } from 'zod';

/** One Active desk user's book shape. */
export const bookHealthDeskUserSchema = z.object({
    userId: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    /** manager_aliases rows bound to this user. */
    aliasCount: z.number(),
    /** The senior this user acts for (SEP-12), or null for a primary IM. */
    actingForUserId: z.string().nullable(),
    actingForName: z.string().nullable(),
    /** Canonical clients in the user's RESOLVED book (a junior -> the senior). */
    bookClientCount: z.number(),
    /** Canonical clients attributed to this user's OWN id (hidden when acting-for). */
    ownBookClientCount: z.number(),
});
export type BookHealthDeskUser = z.infer<typeof bookHealthDeskUserSchema>;

/** A grid manager string that resolves to no IM (no_match) or two (ambiguous). */
export const bookHealthUnresolvedManagerSchema = z.object({
    investmentManager: z.string(),
    portfolioCount: z.number(),
    reason: z.enum(['no_match', 'ambiguous']),
    /** For `ambiguous`, the colliding candidate user ids (empty for no_match). */
    candidateUserIds: z.array(z.string()),
});
export type BookHealthUnresolvedManager = z.infer<typeof bookHealthUnresolvedManagerSchema>;

/** A canonical client whose stored IM differs from what the rule resolves now. */
export const bookHealthStaleAttributionSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    storedUserId: z.string().nullable(),
    resolvedUserId: z.string().nullable(),
    reason: z.enum(['no_match', 'ambiguous', 'mismatch']),
});
export type BookHealthStaleAttribution = z.infer<typeof bookHealthStaleAttributionSchema>;

export const bookHealthResponseSchema = z.object({
    deskUsers: z.array(bookHealthDeskUserSchema),
    unresolvedManagers: z.array(bookHealthUnresolvedManagerSchema),
    staleAttributions: z.array(bookHealthStaleAttributionSchema),
});
export type BookHealthResponse = z.infer<typeof bookHealthResponseSchema>;
